import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { useFieldArray, Controller, Control } from "react-hook-form";
import { Trash2, PlusCircle, HelpCircle } from "lucide-react";
import { FormLabel } from "@ui/internal/components/label";

interface FaqContainerProps {
  control: Control<any>;
}

const FaqContainer = ({ control }: FaqContainerProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "faqs",
  });

  return (
    <div className="w-full space-y-6 mt-10">
      <div className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="p-5 rounded-md border bg-white shadow-sm relative group animate-in fade-in slide-in-from-top-2"
          >
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => remove(index)}
              >
                <Trash2 size={18} />
              </Button>
            </div>

            <div className="space-y-4">
              <Controller
                control={control}
                name={`faqs.${index}.question`}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <FormLabel label={`Question ${index + 1}`} />
                    <Input {...field} placeholder="Enter FAQ question" />
                    {error && <span className="text-xs text-red-500">{error.message}</span>}
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`faqs.${index}.answer`}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <FormLabel label="Answer" />
                    <Textarea 
                      {...field} 
                      placeholder="Enter FAQ answer" 
                      className="min-h-[100px] resize-none"
                    />
                    {error && <span className="text-xs text-red-500">{error.message}</span>}
                  </div>
                )}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center pt-4">
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-2 border-[#1DA077] text-[#1DA077] hover:bg-[#1DA077] hover:text-white transition-all"
          onClick={() =>
            append({
              question: "",
              answer: "",
            })
          }
        >
          <PlusCircle size={18} />
          Add FAQ
        </Button>
      </div>
    </div>
  );
};

export default FaqContainer;
